<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import {
  hasSelectedInbox,
  isInboxSelected,
  toggleInboxSelection,
} from 'dashboard/mostChatwootExtensions/cannedResponseAccess';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edInboxIds: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      inboxIds: [...this.edInboxIds],
      show: true,
    };
  },
computed: {
  ...mapGetters({
    inboxes: "inboxes/getInboxes",
  }),
  pageTitle() {
    return `${this.$t("CANNED_MGMT.EDIT.TITLE")} - ${this.edshortCode}`;
  },
  hasSelectedInbox() {
    return hasSelectedInbox(this.inboxIds);
  },
},
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
toggleInbox(inboxId) {
  this.inboxIds = toggleInboxSelection(this.inboxIds, inboxId);
},
isInboxSelected(inboxId) {
  return isInboxSelected(this.inboxIds, inboxId);
},
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.inboxIds = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
editCannedResponse() {
  if (!this.hasSelectedInbox) {
    useAlert("Selecione pelo menos uma caixa postal.");
    return;
  }

      // Show loading on button
      this.editCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          content: this.content,
          inbox_ids: this.inboxIds,
        })
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>
<div class="w-full mt-4">
  <p class="mb-2 text-sm font-medium text-n-slate-12">
    Disponível nas caixas
  </p>

  <div class="flex flex-col gap-2">
    <button
      v-for="inbox in inboxes"
      :key="inbox.id"
      type="button"
      class="p-2 rounded-md border border-solid flex items-center justify-between text-start"
      :class="
        isInboxSelected(inbox.id)
          ? 'bg-n-blue-2 dark:bg-n-blue-1 border-n-blue-3 dark:border-n-blue-4'
          : 'bg-white dark:bg-n-solid-2 border-n-weak dark:border-n-strong'
      "
      @click="toggleInbox(inbox.id)"
    >
      <span class="text-sm text-n-slate-12">
        {{ inbox.name }}
      </span>

      <span v-if="isInboxSelected(inbox.id)">✓</span>
    </button>
  </div>

  <p v-if="!hasSelectedInbox" class="mt-2 text-sm text-n-ruby-9">
    Selecione pelo menos uma caixa postal.
  </p>
</div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              !hasSelectedInbox ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];
}
</style>
